// SbAssistConfig.js
// Page-context greetings and suggestion chips for SB Assist.
// ─────────────────────────────────────────────────────────────────────────────
// Extend PAGE_CONTEXT_MAP / SUGGESTED_PROMPTS whenever a new route is added.
// OUT-OF-SCOPE modules (Skills Bridger manager, Talent Mobility, Learning Catalyst,
// Onboarding) are commented out below — re-enable when those modules ship in v1.




// ─── Page Context ─────────────────────────────────────────────────────────────
import { APP_ROUTES } from "constants/appRoutes.constants";

const ALLIE_INTRO = "Hi {name}, I am Allie, your Skillsbridge.AI virtual assistant. I can:";

export const PAGE_CONTEXT_MAP = {
  [APP_ROUTES.DASHBOARD]: {
    displayName: "Dashboard",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.JOBS_LIBRARY]: {
    displayName: "Jobs Library",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.JOB_DETAILS]: {
    displayName: "Job Details",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.PEOPLE_AI]: {
    displayName: "People + AI",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.PAIC_GROUP_AGENTS]: {
    displayName: "PAIC Group Agents",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.PAIC_AGENT_DETAILS]: {
    displayName: "Agent Details",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.PAIC_ROLE_IMPACT]: {
    displayName: "Role Impact",
    greeting: ALLIE_INTRO,
  },

  [APP_ROUTES.SKILLS_BRIDGER]: {
    displayName: "Skills Bridger",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.SKILLS_MAP]: {
    displayName: "Skills Map",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.GROUP_SKILLS_MAP]: {
    displayName: "Group Skills Map",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.EMPLOYEE_SKILLS_MAP]: {
    displayName: "Employee Skills Map",
    greeting: ALLIE_INTRO,
  },

  [APP_ROUTES.ADMIN_SETUP]: {
    displayName: "Admin Console",
    greeting: ALLIE_INTRO,
  },
  [APP_ROUTES.SYSTEM_ADMIN_SETUP]: {
    displayName: "System Admin",
    greeting: ALLIE_INTRO,
  },

  // OUT OF SCOPE — v1. Re-enable when module ships.
  // "/talent-mobility": {
  //   displayName: "Talent Mobility",
  //   greeting: "Hi {name} 👋 How can I assist you with **Talent Mobility**?",
  // },
  // "/learning-catalyst": {
  //   displayName: "Learning Catalyst",
  //   greeting: "Hi {name} 👋 Ready to grow? Let me help you with **Learning Catalyst**!",
  // },
};

export const DEFAULT_CONTEXT = {
  displayName: null,
  greeting: ALLIE_INTRO,
};

export const CAPABILITIES_INTRO = `
- Answer questions about **Jobs Library** — JD creation, statuses, and approval workflows
- Help with **People + AI (PAIC)** — evaluations, agent selection, and AI results
- Guide you through **platform navigation** and explain how features work
- Clarify any **SkillsBridge term**, status, or metric

What would you like me to help you with today?`;

// ─── Suggested Prompts ────────────────────────────────────────────────────────
export const SUGGESTED_PROMPTS = {
  [APP_ROUTES.JOBS_LIBRARY]: [
    "How do I create a Job Description?",
    "What is the approval workflow?",
    "What do the JD statuses mean?",
    "How do I give feedback?",
    "What can you help with?",
  ],
  [APP_ROUTES.JOB_DETAILS]: [
    "How do I edit a Job Description?",
    "How do I send a JD for approval?",
    "How do I compare two JD versions?",
    "How do I report an error?",
    "What can you help with?",
  ],
  [APP_ROUTES.PEOPLE_AI]: [
    "How do I create a PAIC group?",
    "How do I run a PAIC evaluation?",
    "How long does PAIC evaluation take?",
    "How do I give feedback?",
    "What can you help with?",
  ],
  [APP_ROUTES.PAIC_GROUP_AGENTS]: [
    "How do I select agents for a version?",
    "What does AI Potential % mean?",
    "How do I create a new version?",
    "How do I report an error?",
    "What can you help with?",
  ],
  [APP_ROUTES.PAIC_AGENT_DETAILS]: [
    "What is an Agent Job Description?",
    "What is a PAIC agent?",
    "How do I interpret agent metrics?",
    "How do I give feedback?",
    "What can you help with?",
  ],
  [APP_ROUTES.PAIC_ROLE_IMPACT]: [
    "What does Role Impact show?",
    "How do I read the task breakdown?",
    "What does AI Potential % mean for a role?",
    "How do I report an error?",
    "What can you help with?",
  ],
  [APP_ROUTES.DASHBOARD]: [
    "What is Jobs Library?",
    "What is People + AI (PAIC)?",
    "How do I configure my organisation?",
    "How do I report an error?",
    "What can you help with?",
  ],
  // Route-neutral fallback: used only when a page has no specific prompt set.
  // Keep this generic (not Jobs-Library-flavoured) so it reads correctly on any
  // page — including the Admin Console — that isn't otherwise mapped.
  default: [
    "What can you help me with?",
    "How do I navigate SkillsBridge?",
    "Explain a SkillsBridge term or status",
    "How do I give feedback?",
  ],

  [APP_ROUTES.ADMIN_SETUP]: [
    "How do I set up my organisation?",
    "How do I upload job descriptions?",
    "How do I manage job codes?",
    "How do I create a profile or manage access?",
    "What can you help with?",
  ],
  [APP_ROUTES.SYSTEM_ADMIN_SETUP]: [
    "What can I do in the Admin Console?",
    "How do I manage the org structure and legal entities?",
    "How do I manage employees?",
    "How do I manage compensation settings?",
    "What can you help with?",
  ],

  [APP_ROUTES.SKILLS_BRIDGER]: [
    "What is my SB Index?",
    "How do I improve my score?",
    "What are my skill gaps?",
    "How do I upload source files?",
    "What can you help with?",
  ],
  [APP_ROUTES.SKILLS_MAP]: [
    "What does the Skills Map show?",
    "What is my SB Index?",
    "What are my skill gaps?",
    "How do I improve my score?",
    "What can you help with?",
  ],
  [APP_ROUTES.GROUP_SKILLS_MAP]: [
    "What does Group Skills Map show?",
    "How do I update team proficiency?",
    "What do the performance levels mean?",
    "How do I improve my team's readiness?",
    "What can you help with?",
  ],
  [APP_ROUTES.EMPLOYEE_SKILLS_MAP]: [
    "What does this employee's skills map show?",
    "What is the SB Index?",
    "What do the performance levels mean?",
    "How do I update proficiency?",
    "What can you help with?",
  ],

  // OUT OF SCOPE — v1. Re-enable when module ships.
  // "/talent-mobility": [...],
  // "/learning-catalyst": [...],
};

// ─── Route resolution ─────────────────────────────────────────────────────────
// Match by longest matching route prefix rather than an exact pathname lookup,
// so child/detail routes (e.g. /admin-setup/compensation, or a job-details path
// with :id params) resolve to their parent's config instead of falling through
// to the generic default. Exact matches naturally win because they are the
// longest possible match.
function longestPrefixKey(map, pathname) {
  let best = null;
  for (const key of Object.keys(map)) {
    if (key === "default") continue;
    if (pathname === key || pathname.startsWith(`${key}/`)) {
      if (best === null || key.length > best.length) best = key;
    }
  }
  return best;
}

export function resolvePageContext(pathname) {
  const key = longestPrefixKey(PAGE_CONTEXT_MAP, pathname || "");
  return key ? PAGE_CONTEXT_MAP[key] : DEFAULT_CONTEXT;
}

export function resolveSuggestions(pathname) {
  const key = longestPrefixKey(SUGGESTED_PROMPTS, pathname || "");
  return key ? SUGGESTED_PROMPTS[key] : SUGGESTED_PROMPTS.default;
}

